// src/utils/pdf-shell.js — branded jsPDF shell for DropPin PDFs.
// ---------------------------------------------------------------------
// Ported from InSpec's api/shared/insite-pdf-shell.js (v2.34.88, 262 lines).
// InSpec's version was HTML→CloudConvert→PDF (server-side). DropPin runs
// PDFs entirely in the browser via jsPDF, so the shell is rebuilt as a
// PdfShell class that mutates a jsPDF instance directly rather than
// emitting an HTML string.
//
// Multi-tenant ready from day one (see feedback_sellable_from_day_one in
// MEMORY.md). Every brand-customisable bit lives in opts:
//   brandName    — display name on cover + footer (default 'DropPin')
//   brandColor   — accent colour, hex (default DropPin orange #fb923c)
//   logoDataUrl  — base64 data URL or null; null falls back to text logo
//   tagline      — small line under brand on the cover
//   docLabel     — e.g. 'Defect Report'
//   confidential — small print on the cover footer strip
//   generator    — name in the bottom-of-every-page footer
//   footerExtra  — optional extra footer text (ABN, registration, etc.)
//
// v0.7.0 — initial port; cover page + header + page footer.

const MM_TO_PT = 2.83465;
const PAGE_W_MM = 210;
const PAGE_H_MM = 297;

// Default brand profile. v1.0.0 will pull this from /api/tenants/:id;
// for now ProjectDetail / ReportScreen hardcodes tenantId=1.
export const DEFAULT_BRAND = {
  brandName:    'DropPin',
  brandColor:   '#fb923c',
  logoDataUrl:  null,
  tagline:      'Drop a plan. Drop a pin. Send a report.',
  docLabel:     'Defect Report',
  confidential: 'Confidential — for project parties only',
  generator:    'DropPin',
  footerExtra:  '',
  // v0.222.0 — Brett: "change droppin.app". It was never DropPin's address, and
  // it printed on every cover. The address now comes from where the report was
  // made (reportSiteHost in reportPdf.js); a PDF built without it names no site
  // rather than a wrong one.
  website:      '',
  // v0.45.30 — Cover theme key. Three options:
  //   'cinematic' — dark navy gradient + orange accent (the original look)
  //   'light'     — white cover, navy headlines, brand-orange accent line
  //   'mono'      — black + white, single accent rule, data-first minimal
  // Picked on the report Settings panel; passed through to the PDF
  // shell when the report is built. Anything else falls back to
  // cinematic so we never break on a stale settings JSON.
  theme: 'cinematic',
};

// v0.45.30 — Theme palette table. Each preset defines the cover-page
// chrome only — body pages share the same compact header/footer.
//
// Fields:
//   bandsRgb         — array of { yFrac, color: [r,g,b] } stops painted
//                      top-to-bottom on the cover. For solid backgrounds,
//                      pass a single stop at yFrac: 1.
//   coverTextRgb     — main text colour on the cover (headlines, brand).
//   coverMutedRgb    — secondary text (tagline, meta labels).
//   accentStrip      — true → 8mm right-edge brand strip; false → no strip.
//   accentRulePx     — height of a horizontal accent rule under the doc
//                      label (null/0 = no rule). Mono theme uses this.
//   wordmarkBg       — the colour passed as bgColor to the wordmark
//                      renderer when masking the typed "i".
//   chipFillRgb      — RGB of the chip background.
//   chipTextRgb      — RGB of the chip text.
//   kpiCellOpacity   — opacity of the KPI cell background.
//   kpiValueRgb      — default value colour (overridden by per-KPI
//                      `color`).
//   metaLabelRgb     — meta-grid label colour.
//   metaValueRgb     — meta-grid value colour.
//   metaRuleRgb      — divider line.
export const COVER_THEMES = {
  cinematic: {
    bandsRgb: [
      { yFrac: 0.55, color: [15, 23, 42] },     // top dark navy
      { yFrac: 0.85, color: [30, 41, 59] },     // mid slate
      { yFrac: 1.00, color: [45, 45, 45] },     // bottom near-black
    ],
    coverTextRgb:   [255, 255, 255],
    coverMutedRgb:  [200, 200, 200],
    accentStrip:    true,
    accentRulePx:   0,
    wordmarkBg:     null,            // dark surface — leave the masking alone
    chipFillRgb:    [255, 255, 255], // chip drawn with low opacity below
    chipTextRgb:    [255, 255, 255],
    kpiCellOpacity: 0.06,
    kpiValueRgb:    [255, 255, 255],
    metaLabelRgb:   [200, 200, 200],
    metaValueRgb:   [255, 255, 255],
    metaRuleRgb:    [255, 255, 255],
  },
  light: {
    bandsRgb: [
      { yFrac: 1.00, color: [253, 251, 247] },  // soft cream-white
    ],
    coverTextRgb:   [15, 23, 42],     // navy
    coverMutedRgb:  [100, 116, 139],  // slate-500
    accentStrip:    true,
    accentRulePx:   0,
    wordmarkBg:     [253, 251, 247],  // mask the typed "i" with bg
    chipFillRgb:    [241, 245, 249],  // slate-100
    chipTextRgb:    [15, 23, 42],
    kpiCellOpacity: 1.0,
    kpiValueRgb:    [15, 23, 42],
    metaLabelRgb:   [100, 116, 139],
    metaValueRgb:   [15, 23, 42],
    metaRuleRgb:    [203, 213, 225],  // slate-300
  },
  mono: {
    bandsRgb: [
      { yFrac: 1.00, color: [255, 255, 255] },  // pure white
    ],
    coverTextRgb:   [10, 10, 10],
    coverMutedRgb:  [115, 115, 115],
    accentStrip:    false,
    accentRulePx:   1.2,              // single horizontal rule under doc label
    wordmarkBg:     [255, 255, 255],
    chipFillRgb:    [245, 245, 245],
    chipTextRgb:    [10, 10, 10],
    kpiCellOpacity: 0,                // no cell background — just text
    kpiValueRgb:    [10, 10, 10],
    metaLabelRgb:   [115, 115, 115],
    metaValueRgb:   [10, 10, 10],
    metaRuleRgb:    [200, 200, 200],
  },
};

function resolveTheme(key) {
  return COVER_THEMES[key] || COVER_THEMES.cinematic;
}

// Hex → {r,g,b} for jsPDF setFillColor (which wants three numbers).
// Tolerates short hex (#abc) the same way InSpec did.
export function hexToRgb(hex) {
  const h = String(hex || '#fb923c').replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
}

/**
 * v0.292.0 — where an image of iw x ih sits inside a boxW x boxH box at its
 * own aspect ratio: as large as fits, flush left, centred on the box's
 * height. Unknown or bad sizes fill the box (the pre-v0.292 behaviour).
 */
export function fitLogoBox(iw, ih, boxW, boxH) {
  const w0 = Number(iw), h0 = Number(ih);
  if (!(w0 > 0) || !(h0 > 0)) return { x: 0, y: 0, w: boxW, h: boxH };
  const k = Math.min(boxW / w0, boxH / h0);
  const mm = (v) => Math.round(v * 1000) / 1000; // 0.001 mm: finer than jsPDF prints
  const w = mm(w0 * k), h = mm(h0 * k);
  return { x: 0, y: Math.max(0, mm((boxH - h) / 2)), w, h };
}

export function fmtDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
}

// ---------------------------------------------------------------------
// PdfShell — wraps a jsPDF instance with branded chrome helpers.
//
// Usage:
//   const shell = new PdfShell(doc, { brandName: 'DropPin', ... });
//   shell.drawCover({ docTitle, ref, mode, customer, location, status,
//                     reportDate, kpis });
//   doc.addPage();   // shell.cursor stays in sync via shell.newPage()
//   shell.drawPageHeader({ docType: 'DEFECT REPORT', docNumber: ref,
//                          docTitle: jobTitle });
//   // ... caller writes body content using shell.text/heading helpers
//   shell.drawAllFooters();   // call ONCE at the very end
// ---------------------------------------------------------------------
export class PdfShell {
  constructor(doc, opts = {}) {
    this.doc = doc;
    this.opts = { ...DEFAULT_BRAND, ...opts };
    this.brandRgb = hexToRgb(this.opts.brandColor);
    // v0.103.0 — Report layout style tokens (see utils/report-styles.js).
    // null/undefined = the Signature (legacy) pipeline: every branch
    // below falls back to the exact pre-style behaviour.
    this.style = opts.styleTokens || null;
    // The cover palette key: a style may pin its own base palette
    // (e.g. Professional pins 'light'); Signature keeps following the
    // legacy settings.theme picker.
    this.coverKey = (this.style && this.style.cover && this.style.cover.themeKey) || this.opts.theme;
    // v0.45.30 — Resolve the cover theme. Falls back to cinematic for
    // any unknown key so a stale settings JSON can't break the build.
    this.theme = resolveTheme(this.coverKey);
    // A4 page margins (mm) — kept in sync with the InSpec @page rule.
    this.margin = { top: 18, right: 14, bottom: 22, left: 14 };
    // Cursor that body-content helpers advance as they render.
    this.cursorY = this.margin.top;
    this.contentWidth = PAGE_W_MM - this.margin.left - this.margin.right;
  }

  // v0.103.0 — Resolve a style token colour spec to [r,g,b].
  // 'ACCENT' → the brand colour, 'BLUE' → the pin-head blue, arrays
  // pass through, anything else → fallback.
  resolveRgb(spec, fallback) {
    if (spec === 'ACCENT') return [this.brandRgb.r, this.brandRgb.g, this.brandRgb.b];
    if (spec === 'BLUE') return [59, 130, 246];
    if (Array.isArray(spec)) return spec;
    return fallback;
  }

  // -------------------------------------------------------------------
  // v0.45.14 — Branded wordmark renderer for the PDF.
  // v0.45.18 — Brett: "literally grab one of the pins and replace the i".
  //
  // The web wordmark (<DropPinWordmark />) renders the "i" as the actual
  // 3D pushpin SVG from the plan canvas — angled grey stem, glossy
  // orange head, shadow at the tip. This is the PDF equivalent:
  //   1. Erase the typed "i" by overdrawing it in the surrounding
  //      background colour (defaults to transparent — caller supplies
  //      bgColor when rendering on a non-white surface like the cover).
  //   2. Draw the angled pin glyph in primitives at the i's column.
  //
  // Falls back to plain text when the brand name isn't "DropPin" so
  // white-label tenants keep their own brand intact.
  //
  // Returns the rendered text width so callers can position what comes
  // next (e.g. the tagline beneath).
  //
  // Coords are in mm. fontSize is in pt. dotColor defaults to brand
  // orange (#fb923c) to match the canvas pins; bgColor is the colour
  // we use to mask the original "i" glyph.
  drawBrandedWordmark(x, y, opts = {}) {
    const doc = this.doc;
    const {
      fontSize = 14,
      weight   = 'bold',
      color    = null,
      dotColor = [251, 146, 60],  // brand orange — canvas pin default
      bgColor  = null,            // pass [r,g,b] when surface isn't white
    } = opts;
    const text = this.opts.brandName || 'DropPin';

    doc.setFont('helvetica', weight);
    doc.setFontSize(fontSize);
    if (color) doc.setTextColor(color[0], color[1], color[2]);
    doc.text(text, x, y);
    const totalW = doc.getTextWidth(text);

    // Only swap the i for a pin when the brand name is "DropPin".
    if (text !== 'DropPin') return totalW;

    // Width of "DropP" places us at the i's left edge.
    const prefixW = doc.getTextWidth('DropP');
    const iW      = doc.getTextWidth('i');
    const iLeftMm = x + prefixW;

    const ptToMm     = 0.3528;
    const capHeight  = fontSize * ptToMm * 0.72;  // approx helvetica cap height in mm

    // Mask the typed i. Without a bgColor we skip the mask (the pin
    // is wider than the i so it largely covers it anyway, and over-
    // drawing white on a dark cover would be worse than the residual).
    if (bgColor) {
      doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
      // Box covers the i's stem + tittle area generously.
      doc.rect(
        iLeftMm - 0.3,
        y - capHeight - 1,
        iW + 0.6,
        capHeight + 2,
        'F'
      );
    }

    // ---- draw the pin glyph at the i column ----
    // Pin geometry mirrors the canvas SVG (viewBox 40×60, tip at 12,56,
    // head at 22,18 r=12). Scale that into a box H_mm tall × W_mm wide.
    const H_mm = capHeight * 1.30;            // pin slightly taller than cap
    const W_mm = H_mm * (40 / 60);            // preserve canvas aspect
    // Tip of the pin sits on the text baseline, horizontally centred
    // under the i's column.
    const cx0  = iLeftMm + iW / 2;
    const tipX = cx0;
    const tipY = y;
    // Convert canvas SVG units (1 unit = H_mm/60 mm).
    const u    = H_mm / 60;
    // Canvas tip is at (12, 56); other anchors are offsets from there.
    // Centre of head: (22-12, 18-56) = (+10, -38) units from tip.
    const headCx = tipX + 10 * u;
    const headCy = tipY - 38 * u;
    const headR  = 12 * u;
    // Stem endpoints: (12,55) → (22,24), so (0,-1) → (+10,-32) from tip.
    const stemX1 = tipX +  0 * u;
    const stemY1 = tipY -  1 * u;
    const stemX2 = tipX + 10 * u;
    const stemY2 = tipY - 32 * u;

    // Shadow ellipse on the baseline.
    doc.setFillColor(0, 0, 0);
    doc.setGState && doc.setGState(new doc.GState({ opacity: 0.35 }));
    doc.ellipse(tipX, tipY, 7 * u, 1.8 * u, 'F');
    doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));

    // Stem (angled needle).
    doc.setDrawColor(91, 100, 112);   // #5b6470
    doc.setLineWidth(2.6 * u);
    doc.setLineCap && doc.setLineCap('round');
    doc.line(stemX1, stemY1, stemX2, stemY2);

    // Stem highlight.
    doc.setDrawColor(255, 255, 255);
    doc.setGState && doc.setGState(new doc.GState({ opacity: 0.35 }));
    doc.setLineWidth(0.8 * u);
    doc.line(stemX1 + 0.5 * u, stemY1 - 1 * u, stemX2 - 0.5 * u, stemY2);
    doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));

    // Head (sphere). Subtle outline keeps it crisp on any background.
    doc.setFillColor(dotColor[0], dotColor[1], dotColor[2]);
    doc.setDrawColor(0, 0, 0);
    doc.setGState && doc.setGState(new doc.GState({ opacity: 0.25 }));
    doc.setLineWidth(0.4);
    doc.ellipse(headCx, headCy, headR, headR, 'FD');
    doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));

    // Gloss highlight upper-left of the head.
    doc.setFillColor(255, 255, 255);
    doc.setGState && doc.setGState(new doc.GState({ opacity: 0.55 }));
    doc.ellipse(headCx - 4 * u, headCy - 5 * u, 4.5 * u, 2.8 * u, 'F');
    doc.setGState && doc.setGState(new doc.GState({ opacity: 1 }));

    // Reset line state so caller's subsequent draws aren't surprised.
    doc.setLineWidth(0.2);
    doc.setLineCap && doc.setLineCap('butt');

    return totalW;
  }

  // -------------------------------------------------------------------
  // Cover page — full-bleed dark gradient with brand accent strip.
  // Matches the InSpec cover-page layout (logo + docLabel + title + ref
  // chip + KPI strip + meta grid + footer strip). We don't have CSS
  // gradients in jsPDF so the gradient is a vertical stack of
  // increasingly-lighter rectangles. Cheap and convincing.
  // v0.37.0 — `preparedBy` added so the cover can show the operator who
  //           built the report. Optional; renders as a 5th meta cell when
  //           present, no layout impact when absent.
  // v0.40.0 — `reportTeamMembers` (Distribution roster) renders as a small
  //           panel in the lower-third of the cover when present. Each
  //           row: { name, email?, role? }. Hidden when the array is empty.
  drawCover({ docTitle, ref, mode, customer, location, status, reportDate, preparedBy, kpis, reportTeamMembers = [] }) {
    const doc = this.doc;
    const { r, g, b } = this.brandRgb;
    // v0.45.30 — theme palette drives all the cover colours.
    // v0.103.0 — style cover tokens (C) can override individual pieces;
    // with no styleTokens every `C.x ?? legacy` below resolves to the
    // legacy value, so Signature output is unchanged.
    const T = this.theme;
    const C = (this.style && this.style.cover) || {};
    const [tR, tG, tB] = T.coverTextRgb;
    const [mR, mG, mB] = T.coverMutedRgb;
    const coverFont = C.font || 'helvetica';

    // Background bands — paint the cover top-to-bottom using the
    // theme's bands list. yFrac is the BOTTOM of each band as a
    // fraction of the page height. Single-stop themes (light, mono)
    // paint a solid colour. Style tokens may override the bands and
    // use 'ACCENT' as a colour (Bold's top band re-tints live with
    // the chosen brand colour).
    let prevY = 0;
    const bands = C.bands || T.bandsRgb;
    for (const band of bands) {
      const yBottom = PAGE_H_MM * band.yFrac;
      const [br, bg, bb] = this.resolveRgb(band.color, [255, 255, 255]);
      doc.setFillColor(br, bg, bb);
      doc.rect(0, prevY, PAGE_W_MM, yBottom - prevY, 'F');
      prevY = yBottom;
    }

    // Right-edge brand strip (8mm wide) — themes/styles can disable.
    if (C.accentStrip !== undefined ? C.accentStrip : T.accentStrip) {
      doc.setFillColor(r, g, b);
      doc.rect(PAGE_W_MM - 8, 0, 8, PAGE_H_MM, 'F');
    }

    // ---- header row: pin logomark + brand name + tagline ----
    // v0.40.0 — Replaced the wordmark-only header with a proper logomark.
    //           If the caller passes opts.logoDataUrl that wins; otherwise
    //           we draw a pin glyph using jsPDF primitives (orange teardrop
    //           + white inner dot). Cheap, no asset dependency, and
    //           recognisably DropPin.
    // v0.45.31 — Brett (marked-up PDF): the v0.40.0 legacy teardrop pin
    // logomark (orange circle + downward triangle + white inner eye)
    // sat to the LEFT of the wordmark, which already has a real
    // canvas pin as its "i" since v0.45.18. Double pin = wrong.
    // Removed; only a Pro logoDataUrl override keeps drawing an image.
    let y = 26;
    let brandX;
    // v0.215.0 — image data only. The tenant's logo used to arrive as its web
    // address, and jsPDF given an address embeds the TEXT when its own fetch
    // fails (see reportImages.js). ReportScreen now fetches it first.
    const logo = this.opts.logoDataUrl;
    if (typeof logo === 'string' && /^data:image\/(png|jpe?g);base64,/i.test(logo)) {
      try {
        // v0.292.0 — drawn at its OWN shape inside the 24 x 14 mm box (it used
        // to be stretched to fill the box: a wide logo squashed, a tall one
        // smeared). Left-aligned, centred on the box's height.
        let box = { x: 0, y: 0, w: 24, h: 14 };
        try {
          const p = doc.getImageProperties(logo);
          box = fitLogoBox(p && p.width, p && p.height, 24, 14);
        } catch { /* unreadable size: the whole box, as before */ }
        doc.addImage(logo, /^data:image\/png/i.test(logo) ? 'PNG' : 'JPEG',
          this.margin.left + box.x, y - 6 + box.y, box.w, box.h, undefined, 'FAST');
      } catch { /* ignore bad logo data */ }
      brandX = this.margin.left + 30;
    } else {
      // No separate logomark — the wordmark IS the logo (canvas-pin "i").
      brandX = this.margin.left;
    }
    // v0.45.14 — wordmark with blue pin-head dot on the i (cover hero).
    // v0.45.30 — pin-head colour follows the theme. Cinematic uses
    // bright blue on the dark gradient; light/mono use the brand
    // colour so the head pops against the white card.
    // v0.103.0 — style tokens can override the row colour (Bold sits
    // the brand row on an accent band → white text), the pin-head
    // colour, and the "i" mask colour.
    const brandRowRgb = this.resolveRgb(C.brandRowRgb, T.coverTextRgb);
    this.drawBrandedWordmark(brandX, y, {
      fontSize: 18,
      weight: 'bold',
      color: brandRowRgb,
      dotColor: this.resolveRgb(C.dotColor, (this.coverKey === 'cinematic') ? [59, 130, 246] : [r, g, b]),
      bgColor: this.resolveRgb(C.wordmarkBg, T.wordmarkBg),
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    if (C.brandRowRgb) doc.setTextColor(brandRowRgb[0], brandRowRgb[1], brandRowRgb[2]);
    else doc.setTextColor(mR, mG, mB);
    doc.text((this.opts.tagline || '').toUpperCase(), brandX, y + 6);

    // ---- doc label (brand accent), title, ref chip ----
    y = 110;
    const titleAlign = C.titleAlign || 'left';
    const labelText = (this.opts.docLabel || 'DEFECT REPORT').toUpperCase();
    const centreX = PAGE_W_MM / 2;

    // v0.103.0 — Classic: formal double rule ABOVE the label block.
    if (C.formalRules) {
      doc.setDrawColor(tR, tG, tB);
      doc.setLineWidth(0.5);
      doc.line(this.margin.left, y - 10, PAGE_W_MM - this.margin.right, y - 10);
      doc.setLineWidth(0.2);
      doc.line(this.margin.left, y - 8.6, PAGE_W_MM - this.margin.right, y - 8.6);
    }

    if (C.labelPill) {
      // v0.103.0 — Modern: doc label inside an accent-filled pill.
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      const lw = doc.getTextWidth(labelText) + 10;
      doc.setFillColor(r, g, b);
      doc.roundedRect(this.margin.left, y - 6, lw, 9, 4.5, 4.5, 'F');
      doc.setTextColor(255, 255, 255);
      doc.text(labelText, this.margin.left + 5, y);
    } else {
      doc.setTextColor(r, g, b);
      doc.setFont(coverFont, 'bold');
      doc.setFontSize(10);
      if (titleAlign === 'center') doc.text(labelText, centreX, y, { align: 'center' });
      else doc.text(labelText, this.margin.left, y);
    }

    // v0.45.30 — Mono theme draws a single horizontal accent rule
    // under the doc label instead of relying on the side strip.
    // v0.103.0 — style tokens override height + width.
    const ruleH = (C.accentRuleH !== undefined) ? C.accentRuleH : T.accentRulePx;
    if (ruleH > 0) {
      doc.setDrawColor(r, g, b);
      doc.setLineWidth(ruleH);
      const ruleW = C.ruleFullWidth ? this.contentWidth - 10 : 28;
      doc.line(this.margin.left, y + 2.5, this.margin.left + ruleW, y + 2.5);
      doc.setLineWidth(0.2);
    }

    // Title baseline offset scales with the token title size (legacy
    // 36pt keeps the exact 12mm drop).
    y += C.titleSize ? Math.max(12, C.titleSize * 0.36) : 12;
    doc.setTextColor(tR, tG, tB);
    doc.setFont(coverFont, 'bold');
    doc.setFontSize(C.titleSize || 36);
    const titleLineH = (C.titleSize || 36) / 3;
    const titleLines = doc.splitTextToSize(docTitle || 'Untitled job', this.contentWidth - 10);
    if (titleAlign === 'center') doc.text(titleLines, centreX, y, { align: 'center' });
    else doc.text(titleLines, this.margin.left, y);
    y += titleLineH * titleLines.length;

    // v0.103.0 — Classic: closing rule BELOW the title.
    if (C.formalRules) {
      doc.setDrawColor(tR, tG, tB);
      doc.setLineWidth(0.2);
      doc.line(this.margin.left, y + 2, PAGE_W_MM - this.margin.right, y + 2);
      y += 6;
    }

    // ref pill — theme-aware chip. v0.103.0 — chipStyle token:
    //   'ghost'   low-opacity fill (cinematic legacy)
    //   'solid'   filled chip (light/mono legacy)
    //   'outline' hairline border, no fill
    //   'none'    plain text line, no chrome
    if (ref) {
      const chipStyle = C.chipStyle || ((this.coverKey === 'cinematic') ? 'ghost' : 'solid');
      const [cR, cG, cB] = T.chipFillRgb;
      const [tcR, tcG, tcB] = T.chipTextRgb;
      const chipText = `${ref}  •  ${mode || 'Job'}  •  ${reportDate || ''}`;
      // Bold matches the pre-style output (the legacy code inherited
      // the title's bold font state here).
      doc.setFont(coverFont, 'bold');
      doc.setFontSize(10);
      const chipW = doc.getTextWidth(chipText) + 10;
      const chipX = (titleAlign === 'center') ? centreX - chipW / 2 : this.margin.left;
      if (chipStyle === 'ghost') {
        doc.setFillColor(cR, cG, cB);
        doc.setGState(new doc.GState({ opacity: 0.08 }));
        doc.roundedRect(chipX, y, chipW, 8, 2, 2, 'F');
        doc.setGState(new doc.GState({ opacity: 1 }));
      } else if (chipStyle === 'solid') {
        doc.setFillColor(cR, cG, cB);
        doc.roundedRect(chipX, y, chipW, 8, 2, 2, 'F');
      } else if (chipStyle === 'outline') {
        doc.setDrawColor(tcR, tcG, tcB);
        doc.setLineWidth(0.3);
        doc.roundedRect(chipX, y, chipW, 8, 2, 2, 'S');
      }
      doc.setTextColor(chipStyle === 'none' ? mR : tcR, chipStyle === 'none' ? mG : tcG, chipStyle === 'none' ? mB : tcB);
      doc.text(chipText, chipX + 5, y + 5.5);
      y += 14;
    }

    // ---- KPI strip — 5 cells ----
    // v0.103.0 — kpiStyle token: 'cells' (legacy backgrounds), 'plain'
    // (no chrome, just figures), 'bars' (accent tick left of each value).
    if (kpis && kpis.length) {
      const kpiStyle = C.kpiStyle || 'cells';
      const stripW = this.contentWidth - 10;
      const cellW = stripW / kpis.length;
      kpis.forEach((kpi, i) => {
        const cx = this.margin.left + cellW * i;
        // Cell background — theme controls opacity. Mono theme
        // skips the background entirely (kpiCellOpacity = 0).
        if (kpiStyle === 'cells' && T.kpiCellOpacity > 0) {
          const fillRgb = (this.coverKey === 'cinematic')
            ? [255, 255, 255]              // white over dark, low opacity
            : [241, 245, 249];             // slate-100 for light
          doc.setFillColor(fillRgb[0], fillRgb[1], fillRgb[2]);
          doc.setGState(new doc.GState({ opacity: T.kpiCellOpacity }));
          doc.roundedRect(cx + 1, y, cellW - 2, 18, 2, 2, 'F');
          doc.setGState(new doc.GState({ opacity: 1 }));
        }
        let vx = cx + 4;
        if (kpiStyle === 'bars') {
          doc.setFillColor(r, g, b);
          doc.rect(cx + 1, y + 1, 1.4, 16, 'F');
          vx = cx + 5.5;
        }
        const valColor = kpi.color || T.kpiValueRgb;
        doc.setTextColor(valColor[0], valColor[1], valColor[2]);
        doc.setFont(coverFont, 'bold');
        doc.setFontSize(16);
        doc.text(String(kpi.value), vx, y + 8);
        doc.setTextColor(mR, mG, mB);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7);
        doc.text(String(kpi.label || '').toUpperCase(), vx, y + 14);
      });
      y += 26;
    }

    // ---- meta grid (Customer / Location / Status / Reported [+ Prepared by]) ----
    // v0.45.31 — Brett (marked-up PDF): meta labels overlap the
    // distribution panel. Root cause: meta was anchored at
    // PAGE_H_MM-80 (fixed) but with 5 cells (PreparedBy present) it
    // ran 48mm tall, ending at PAGE_H_MM-32 — INSIDE the distribution
    // panel at PAGE_H_MM-50. Fixed: stack from the bottom up.
    //   bottom strip       = PAGE_H_MM-12 to PAGE_H_MM
    //   distribution panel = (when present) reserves DIST_H mm above
    //   meta grid          = sits ABOVE distribution with 8mm padding
    const metaCells = [
      ['Customer',    customer   || '—'],
      ['Location',    location   || '—'],
      ['Status',      status     || '—'],
      ['Reported',    reportDate || ''],
    ];
    if (preparedBy) metaCells.push(['Prepared by', preparedBy]);
    const colW = (this.contentWidth - 10) / 2;
    const metaRows = Math.ceil(metaCells.length / 2);
    const metaH    = 8 + metaRows * 16;   // 8mm header + rows
    const distH    = (Array.isArray(reportTeamMembers) && reportTeamMembers.length > 0) ? 56 : 0;
    const bottomStripH = 12;
    // Meta block bottom edge (above distribution + bottom strip, with
    // 4mm gutter to whichever sits below).
    const metaBlockBottom = PAGE_H_MM - bottomStripH - distH - (distH > 0 ? 4 : 4);
    const metaStartY = metaBlockBottom - metaH;
    y = Math.max(y, metaStartY);

    const [ruleR, ruleG, ruleB] = T.metaRuleRgb;
    doc.setDrawColor(ruleR, ruleG, ruleB);
    if (this.coverKey === 'cinematic') doc.setGState(new doc.GState({ opacity: 0.18 }));
    doc.line(this.margin.left, y, PAGE_W_MM - this.margin.right - 10, y);
    if (this.coverKey === 'cinematic') doc.setGState(new doc.GState({ opacity: 1 }));
    y += 8;
    const [mlR, mlG, mlB] = T.metaLabelRgb;
    const [mvR, mvG, mvB] = T.metaValueRgb;
    metaCells.forEach((m, i) => {
      const row = Math.floor(i / 2);
      const col = i % 2;
      const cx = this.margin.left + col * colW;
      const cy = y + row * 16;
      doc.setTextColor(mlR, mlG, mlB);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.text(m[0].toUpperCase(), cx, cy);
      doc.setTextColor(mvR, mvG, mvB);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      const v = doc.splitTextToSize(String(m[1]), colW - 4);
      doc.text(v[0] || '', cx, cy + 5);
    });

    // v0.40.0 — Distribution list panel. Renders below the meta grid
    //           when the report has a non-empty ReportTeamMembers roster.
    //           Grouped by Role. Sized to fit comfortably above the bottom
    //           strip; truncates with '+N more' if the roster is huge.
    // v0.45.15 — Two-column flow. Previously declared a colW + col vars
    //            but never used them; long rosters truncated with empty
    //            right column. Now properly snakes to the second column
    //            when the first runs out of vertical room.
    if (Array.isArray(reportTeamMembers) && reportTeamMembers.length > 0) {
      const groups = new Map();
      for (const m of reportTeamMembers) {
        const k = m.role || 'Other';
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(m);
      }
      // v0.45.31 — was a fixed PAGE_H_MM-50, which collided with the
      // 5-cell meta grid (Brett's marked-up cover). Distribution now
      // sits at the top of its reserved 56mm block above the bottom
      // strip, regardless of meta grid size.
      const distY = PAGE_H_MM - bottomStripH - distH + 6;
      // v0.45.30 — divider + heading follow the theme palette.
      doc.setDrawColor(ruleR, ruleG, ruleB);
      if (this.coverKey === 'cinematic') doc.setGState(new doc.GState({ opacity: 0.18 }));
      doc.line(this.margin.left, distY - 6, PAGE_W_MM - this.margin.right - 10, distY - 6);
      if (this.coverKey === 'cinematic') doc.setGState(new doc.GState({ opacity: 1 }));
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(mlR, mlG, mlB);
      doc.text('DISTRIBUTION', this.margin.left, distY);

      const colStartY = distY + 4;
      const maxY = PAGE_H_MM - 16;
      const colW = (this.contentWidth - 10) / 2;
      const colX = [this.margin.left, this.margin.left + colW];
      let col = 0;
      let curY = colStartY;
      let truncated = 0;

      // Local helpers — keeps the snaking logic clear without leaking
      // state into the outer scope.
      const advance = (dy) => { curY += dy; };
      const moveToNextColumn = () => {
        if (col >= colX.length - 1) return false;
        col += 1;
        curY = colStartY;
        return true;
      };
      const hasRoom = (dy) => curY + dy <= maxY;

      for (const [role, items] of groups.entries()) {
        // Role heading needs ~4mm + at least one item row.
        if (!hasRoom(8) && !moveToNextColumn()) {
          truncated += items.length;
          continue;
        }
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(mvR, mvG, mvB);
        doc.text(String(role).toUpperCase(), colX[col], curY);
        advance(4);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(mvR, mvG, mvB);
        for (const m of items) {
          if (!hasRoom(3.6) && !moveToNextColumn()) {
            truncated++;
            continue;
          }
          const label = m.email ? `${m.name} (${m.email})` : m.name;
          const short = doc.splitTextToSize(String(label || ''), colW - 4)[0] || '';
          doc.text(short, colX[col], curY);
          advance(3.6);
        }
        advance(2);
      }

      if (truncated > 0) {
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(mlR, mlG, mlB);
        doc.text(`+ ${truncated} more not shown`, this.margin.left, Math.min(curY + 2, maxY));
      }
    }

    // ---- cover bottom strip ----
    // v0.45.30 — Strip styling follows the theme. Cinematic gets a
    // 35% black overlay over the gradient; light/mono get a thin
    // top-rule + no fill so the strip blends into the cover.
    if (this.coverKey === 'cinematic') {
      doc.setFillColor(0, 0, 0);
      doc.setGState(new doc.GState({ opacity: 0.35 }));
      doc.rect(0, PAGE_H_MM - 12, PAGE_W_MM, 12, 'F');
      doc.setGState(new doc.GState({ opacity: 1 }));
    } else {
      doc.setDrawColor(ruleR, ruleG, ruleB);
      doc.setLineWidth(0.4);
      doc.line(this.margin.left, PAGE_H_MM - 12, PAGE_W_MM - this.margin.right, PAGE_H_MM - 12);
    }
    doc.setTextColor(mlR, mlG, mlB);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const left = `${this.opts.brandName}${this.opts.website ? ' · ' + this.opts.website : ''}`;
    doc.text(left, this.margin.left, PAGE_H_MM - 4);
    const right = this.opts.confidential || '';
    const rightW = doc.getTextWidth(right);
    doc.text(right, PAGE_W_MM - this.margin.right - 10 - rightW, PAGE_H_MM - 4);
  }

  // -------------------------------------------------------------------
  // Reset cursor for a fresh body page. Caller still needs to addPage()
  // on the jsPDF instance; this just resets our internal Y tracker.
  newPage() {
    this.cursorY = this.margin.top;
  }

  // -------------------------------------------------------------------
  // Diagonal watermark. Used on Free tier reports so the recipient sees
  // "DROPPIN" stamped across every body page. Pro tier passes
  // includeWatermark=false in the buildDefectReportPdf opts and this
  // method is never called.
  //
  // v0.38.0 — subscription tier infrastructure.
  drawWatermark(text = 'DROPPIN') {
    const doc = this.doc;
    doc.saveGraphicsState && doc.saveGraphicsState();
    try {
      doc.setGState(new doc.GState({ opacity: 0.07 }));
      doc.setTextColor(15, 23, 42); // near-black; works on light backgrounds
      doc.setFont('helvetica', 'bold');
      // Big diagonal stamp — half-page diagonal length.
      doc.setFontSize(96);
      const cx = PAGE_W_MM / 2;
      const cy = PAGE_H_MM / 2;
      // Rough text width so we can centre it. Approximation: 0.55 * fontSize
      // per char in mm. Good enough for visual centring on a watermark.
      const approxW = text.length * (96 * 0.45) * 0.353;
      doc.text(text, cx - approxW / 2, cy, { angle: -28 });
    } finally {
      doc.setGState(new doc.GState({ opacity: 1 }));
      doc.restoreGraphicsState && doc.restoreGraphicsState();
    }
  }

  // -------------------------------------------------------------------
  // Page header — brand rule + docType label + docNumber on the right.
  // Drawn once at the top of every body page. Advances cursorY.
  drawPageHeader({ docType, docNumber, docTitle, reportDate }) {
    const doc = this.doc;
    const { r, g, b } = this.brandRgb;
    // v0.103.0 — style header tokens. Absent = legacy Signature header.
    const H = (this.style && this.style.header) || {};
    const hFont = H.font || 'helvetica';
    const compact = !!H.compact;
    let y = this.margin.top;

    // Brand text logo (no image on body pages — keeps it light).
    // v0.45.14 — wordmark with brand-blue pin-head dot on the i.
    this.drawBrandedWordmark(this.margin.left, y + 3, {
      fontSize: compact ? 11 : 14,
      weight: 'bold',
      color: [r, g, b],
      // Body pages are on white — use the deep brand blue.
      dotColor: [29, 78, 216],
    });

    // doc type + title on the left, doc number + date on the right.
    doc.setTextColor(r, g, b);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(compact ? 7 : 8);
    doc.text((docType || '').toUpperCase(), this.margin.left, y + (compact ? 7.5 : 9));
    if (docTitle) {
      doc.setTextColor(17, 24, 39);
      doc.setFont(hFont, 'bold');
      doc.setFontSize(compact ? 9.5 : 11);
      const titleLines = doc.splitTextToSize(docTitle, this.contentWidth - 50);
      doc.text(titleLines[0] || '', this.margin.left, y + (compact ? 11.5 : 14));
    }

    if (docNumber) {
      doc.setTextColor(17, 24, 39);
      doc.setFont(hFont, 'bold');
      doc.setFontSize(compact ? 9.5 : 11);
      const refW = doc.getTextWidth(docNumber);
      doc.text(docNumber, PAGE_W_MM - this.margin.right - refW, y + 5);
    }
    if (reportDate) {
      doc.setTextColor(107, 114, 128);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(compact ? 8 : 9);
      const dW = doc.getTextWidth(reportDate);
      doc.text(reportDate, PAGE_W_MM - this.margin.right - dW, y + (compact ? 10 : 11));
    }

    // Brand rule. v0.103.0 — rule token: 'accent' (legacy, brand
    // colour), 'grey' (hairline neutral), 'double' (two thin dark
    // rules — Classic). Width via ruleW.
    const ruleKind = H.rule || 'accent';
    const ruleY = y + (compact ? 13.5 : 18);
    if (ruleKind === 'grey') {
      doc.setDrawColor(203, 213, 225);
    } else if (ruleKind === 'double') {
      doc.setDrawColor(31, 41, 55);
    } else {
      doc.setDrawColor(r, g, b);
    }
    doc.setLineWidth(H.ruleW !== undefined ? H.ruleW : 0.8);
    doc.line(this.margin.left, ruleY, PAGE_W_MM - this.margin.right, ruleY);
    if (ruleKind === 'double') {
      doc.setLineWidth(0.15);
      doc.line(this.margin.left, ruleY + 1.1, PAGE_W_MM - this.margin.right, ruleY + 1.1);
    }
    doc.setLineWidth(0.2);

    this.cursorY = ruleY + (compact ? 4.5 : 6);

    // v0.38.0 — Free-tier watermark. Caller sets opts.watermark='DROPPIN'
    //           (or any string) on the PdfShell constructor; we stamp it
    //           on every body page that has a header. Pro-tier callers
    //           leave opts.watermark unset and never see this.
    if (this.opts.watermark) {
      this.drawWatermark(this.opts.watermark);
    }
  }

  // -------------------------------------------------------------------
  // Section heading helper. Auto-paginates when near the bottom.
  // v0.40.0 — Heading-to-table spacing widened. Brett's brief was that
  //           Snapshot / By drawing / By trade didn't sit cleanly with
  //           the tables below. Extra 3mm above, extra 2mm below the
  //           underline rule. Cheap, big readability win.
  heading(text, size) {
    // v0.103.0 — style heading tokens: font / size / decoration.
    //   'rule'      hairline underline (legacy Signature look)
    //   'bar'       solid accent bar left of the text (Bold)
    //   'underline' short accent underline (Modern)
    //   'double'    two rules under small-caps text (Classic)
    //   'plain'     no decoration
    const HD = (this.style && this.style.heading) || {};
    const hSize = size || HD.size || 14;
    const hFont = HD.font || 'helvetica';
    const deco = HD.deco || 'rule';
    const { r, g, b } = this.brandRgb;
    this.cursorY += 3;
    this.ensureSpace(16);
    const doc = this.doc;
    doc.setTextColor(15, 23, 42);
    doc.setFont(hFont, 'bold');
    doc.setFontSize(hSize);
    const shown = HD.caps ? String(text).toUpperCase() : text;
    let tx = this.margin.left;
    if (deco === 'bar') {
      doc.setFillColor(r, g, b);
      doc.rect(this.margin.left, this.cursorY - hSize * 0.32, 2.2, hSize * 0.42, 'F');
      tx = this.margin.left + 4.5;
    }
    doc.text(shown, tx, this.cursorY);
    this.cursorY += hSize * 0.45 + 2;
    if (deco === 'rule') {
      doc.setDrawColor(229, 231, 235);
      doc.setLineWidth(0.2);
      doc.line(this.margin.left, this.cursorY, PAGE_W_MM - this.margin.right, this.cursorY);
    } else if (deco === 'underline') {
      doc.setDrawColor(r, g, b);
      doc.setLineWidth(1);
      doc.line(this.margin.left, this.cursorY, this.margin.left + 22, this.cursorY);
      doc.setLineWidth(0.2);
    } else if (deco === 'double') {
      doc.setDrawColor(31, 41, 55);
      doc.setLineWidth(0.4);
      doc.line(this.margin.left, this.cursorY, PAGE_W_MM - this.margin.right, this.cursorY);
      doc.setLineWidth(0.15);
      doc.line(this.margin.left, this.cursorY + 1, PAGE_W_MM - this.margin.right, this.cursorY + 1);
    }
    this.cursorY += 6;
  }

  // Body text helper. Wraps to contentWidth. Returns final cursorY.
  // Body text helper. Wraps to contentWidth. Returns final cursorY.
  text(s, opts = {}) {
    const doc = this.doc;
    const size = opts.size || 10;
    const color = opts.color || [31, 41, 55];
    const bold = opts.bold || false;
    // v0.103.0 — body font follows the style (Classic runs serif).
    const bodyFont = (this.style && this.style.body && this.style.body.font) || 'helvetica';
    doc.setFont(bodyFont, bold ? 'bold' : 'normal');
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const lines = doc.splitTextToSize(String(s || ''), opts.width || this.contentWidth);
    this.ensureSpace(lines.length * (size * 0.4 + 0.5));
    doc.text(lines, opts.x || this.margin.left, this.cursorY);
    this.cursorY += lines.length * (size * 0.4 + 0.5);
    return this.cursorY;
  }

  // Pagination guard — call before drawing a block of given height.
  // If the block would overflow, we add a page + redraw the header.
  ensureSpace(neededMm, headerOpts) {
    if (this.cursorY + neededMm > PAGE_H_MM - this.margin.bottom) {
      this.doc.addPage();
      this.newPage();
      if (headerOpts) this.drawPageHeader(headerOpts);
    }
  }

  // -------------------------------------------------------------------
  // Per-page footer. Called once at the very end so we know the final
  // page count. Iterates every body page (skips the cover, which has
  // its own bottom strip).
  drawAllFooters({ startPage = 2 } = {}) {
    const doc = this.doc;
    // v0.103.0 — style footer tokens: font / italic / rule / size.
    // Same content in every style — only the dressing changes.
    const F = (this.style && this.style.footer) || {};
    const fFont = F.font || 'helvetica';
    const fStyle = F.italic ? 'italic' : 'normal';
    const fSize = F.size || 8;
    const ruleKind = F.rule || 'hair';
    const { r, g, b } = this.brandRgb;
    const total = doc.internal.getNumberOfPages();
    const footnote = `${this.opts.brandName} · Generated by ${this.opts.generator}${this.opts.footerExtra ? ' · ' + this.opts.footerExtra : ''}`;
    for (let p = startPage; p <= total; p++) {
      doc.setPage(p);
      // Rule above the footer — hairline neutral (legacy), accent, or none.
      if (ruleKind !== 'none') {
        if (ruleKind === 'accent') {
          doc.setDrawColor(r, g, b);
          doc.setLineWidth(0.8);
        } else {
          doc.setDrawColor(229, 231, 235);
          doc.setLineWidth(0.2);
        }
        doc.line(this.margin.left, PAGE_H_MM - this.margin.bottom + 6,
                 PAGE_W_MM - this.margin.right, PAGE_H_MM - this.margin.bottom + 6);
        doc.setLineWidth(0.2);
      }
      doc.setTextColor(156, 163, 175);
      doc.setFont(fFont, fStyle);
      doc.setFontSize(fSize);
      doc.text(footnote, this.margin.left, PAGE_H_MM - 8);
      const pageStr = `Page ${p - startPage + 1} of ${total - startPage + 1}`;
      const w = doc.getTextWidth(pageStr);
      doc.text(pageStr, PAGE_W_MM - this.margin.right - w, PAGE_H_MM - 8);
    }
  }
}

export const PAGE = { widthMm: PAGE_W_MM, heightMm: PAGE_H_MM, mmToPt: MM_TO_PT };
